import { z } from 'zod';

/**
 * Ablängung / Artikel-Varianten (ADR 0040). Reine Hilfsfunktionen ohne Seiteneffekte:
 * abgeleitete Artikelnummer, gedruckter Anhang und Längen-Preisberechnung. Alle
 * Beträge in Mikro-Einheiten (CLAUDE.md §6) — keine Float-Arithmetik.
 */

/** Standard-Trennzeichen zwischen Basisnummer und Längen-Anhang (z. B. `4711-2300`). */
export const CUT_NUMBER_SEPARATOR = '-';

/** Zulässige Länge in mm: ganzzahlig, 1 … 1.000.000 (= 1 km). */
export const cutLengthMmSchema = z.number().int().min(1).max(1_000_000);

/** Eingabe „Variante materialisieren": nur die Fixlänge (Basisartikel kommt aus der URL). */
export const createArticleVariantSchema = z.object({
  lengthMm: cutLengthMmSchema,
});
export type CreateArticleVariantInput = z.infer<typeof createArticleVariantSchema>;

/**
 * Eingabe „Sub-Artikel bearbeiten" (ADR 0040): Ein Sub-Artikel erbt Bezeichnung,
 * Texte, Preise usw. LIVE vom Mutterartikel — editierbar sind ausschließlich die
 * Ablängung (Länge), das Gewicht und der Lagerstand. Alle Felder optional
 * (Teil-Update); Länge ändert Nummer/Anhang/Name mit.
 */
export const updateArticleVariantSchema = z
  .object({
    lengthMm: cutLengthMmSchema,
    weightKg: z.number().min(0).max(100000).nullable(),
    stockQty: z.number().min(0),
    minStock: z.number().min(0).nullable(),
    maxStock: z.number().min(0).nullable(),
  })
  .partial();
export type UpdateArticleVariantInput = z.infer<typeof updateArticleVariantSchema>;

/** Eingabe „Position ablängen": Länge an einer Belegposition (Standard-Weg, kein Artikel). */
export const cutPositionSchema = z.object({
  lengthMm: cutLengthMmSchema,
});
export type CutPositionInput = z.infer<typeof cutPositionSchema>;

/**
 * Vollständige (eindeutige) Variantennummer = Basisnummer + Trennzeichen + Länge in mm.
 * Erfüllt automatisch die Eindeutigkeit je Tenant → gleiche Länge = dieselbe Variante.
 */
export function deriveVariantNumber(
  baseNumber: string,
  lengthMm: number,
  separator: string = CUT_NUMBER_SEPARATOR,
): string {
  return `${baseNumber}${separator}${lengthMm}`;
}

/**
 * Gedruckter Nummer-Anhang, der unter der Basisnummer erscheint — nur die
 * Länge in mm, ohne Label/Einheit (z. B. „2300"), wie bei der Basisnummer
 * selbst zweizeilig gedruckt (Tech-Lead-Vorgabe: kein „L = … mm").
 */
export function formatCutSuffix(lengthMm: number): string {
  return `${lengthMm}`;
}

/** Variantenname = Basisname + Längenzusatz (z. B. „GFK-Balken, 2300 mm"). */
export function deriveVariantName(baseName: string, lengthMm: number): string {
  return `${baseName}, ${lengthMm} mm`;
}

/** Ganzzahlige Division mit kaufmännischer Rundung (halb auf), für positive Beträge robust. */
function roundedDiv(value: bigint, divisor: bigint): bigint {
  if (value >= 0n) return (value + divisor / 2n) / divisor;
  return -((-value + divisor / 2n) / divisor);
}

/**
 * Längen-Preis in Mikro-Einheiten: `Preis-je-Meter × (Länge in mm / 1000) + Pauschale`.
 * `unitPriceAmount` ist der VK je Grundeinheit (Meter). Ergebnis kaufmännisch gerundet.
 */
export function computeCutPriceAmount(
  unitPriceAmount: bigint,
  lengthMm: number,
  surchargeAmount: bigint | null = null,
): bigint {
  const lengthPart = roundedDiv(unitPriceAmount * BigInt(lengthMm), 1000n);
  return lengthPart + (surchargeAmount ?? 0n);
}
